// Use @sentry/browser (not @sentry/react): we only need init + captureException,
// and @sentry/react eagerly touches React.forwardRef at module init, which crashes
// the vendor chunk under Vite's code-splitting. @sentry/browser has no React coupling.
//
// Perf: @sentry/browser is loaded via dynamic import inside initErrorTracking()
// so it ships as its own async chunk instead of ~90 KB inside the critical
// bundle. main.tsx defers init until after window 'load', so the SDK download
// never competes with first paint. captureException/log are safe no-ops until
// the SDK lands; errors thrown before that are lost by design (same tradeoff
// every deferred-Sentry setup makes).
type SentryModule = typeof import('@sentry/browser');

/**
 * Client-side error reporting to GlitchTip (Sentry-protocol compatible).
 *
 * The DSN is delivered at runtime via the `error_tracking_config` settings blob
 * (mirrors the analytics config pattern) — no rebuild needed to change it. Init
 * is idempotent; the first caller with a valid enabled+dsn config wins, and
 * later calls are no-ops. `captureException` is a no-op until initialized.
 */
export interface ErrorTrackingConfig {
  enabled: boolean;
  dsn: string;
  environment?: string;
  /**
   * Ship structured logs to GlitchTip's Logs view (Sentry Logs feature, which
   * GlitchTip ingests over the same envelope endpoint). When on, `console.warn`
   * and `console.error` are also forwarded as log records. Defaults to on
   * whenever error tracking is enabled; set `false` in the config blob to
   * disable just the logs stream without turning off error reporting.
   */
  enableLogs?: boolean;
  /**
   * Performance tracing sample rate (0..1). Drives Sentry.init's tracesSampleRate
   * on both client and server. Defaults to 0.1 when unset. Set 0 to disable
   * tracing while keeping error reporting on.
   */
  tracesSampleRate?: number;
}

let initialized = false;
let sentry: SentryModule | null = null;

export async function initErrorTracking(cfg: Partial<ErrorTrackingConfig> | null | undefined): Promise<void> {
  if (initialized) return;
  if (!cfg || cfg.enabled !== true) return;
  const dsn = typeof cfg.dsn === 'string' ? cfg.dsn.trim() : '';
  if (dsn === '') return;
  initialized = true;
  let Sentry: SentryModule;
  try {
    Sentry = await import('@sentry/browser');
  } catch {
    // Chunk failed to load (offline, ad blocker). Leave `initialized` set so we
    // don't retry on every config refresh; error reporting is best-effort.
    return;
  }
  const logsOn = cfg.enableLogs !== false;
  const release = typeof import.meta.env.VITE_RELEASE === 'string' ? import.meta.env.VITE_RELEASE.trim() : '';
  // Clamp to 0..1 to match the Zod contract — the raw-localStorage fast path
  // (see main.tsx) bypasses /api/config validation, so a stale out-of-range
  // value could otherwise reach Sentry.init.
  const traceRate = typeof cfg.tracesSampleRate === 'number' ? Math.min(1, Math.max(0, cfg.tracesSampleRate)) : 0.1;
  const integrations = [
    ...(logsOn ? [Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] })] : []),
    ...(traceRate > 0 ? [Sentry.browserTracingIntegration()] : []),
  ];
  Sentry.init({
    ...(release ? { release } : {}),
    dsn,
    environment: cfg.environment || 'production',
    tracesSampleRate: traceRate,
    sendDefaultPii: false,
    // Structured logs → GlitchTip Logs page. Forward only console.warn/error on
    // the client to avoid shipping noisy/PII-bearing console.log/info from the
    // browser; intentional logs can still use `log.*` below.
    enableLogs: logsOn,
    integrations,
  });
  sentry = Sentry;
}

/**
 * Structured logger → GlitchTip Logs. No-op until `initErrorTracking` resolves.
 * Prefer this over `console.*` for intentional, queryable client logs:
 *   log.info('checkout started', { plan });   log.error('payment failed', { code });
 */
export const log = {
  trace: (...args: unknown[]) => (sentry?.logger.trace as ((...a: unknown[]) => void) | undefined)?.(...args),
  debug: (...args: unknown[]) => (sentry?.logger.debug as ((...a: unknown[]) => void) | undefined)?.(...args),
  info: (...args: unknown[]) => (sentry?.logger.info as ((...a: unknown[]) => void) | undefined)?.(...args),
  warn: (...args: unknown[]) => (sentry?.logger.warn as ((...a: unknown[]) => void) | undefined)?.(...args),
  error: (...args: unknown[]) => (sentry?.logger.error as ((...a: unknown[]) => void) | undefined)?.(...args),
};

export function captureException(
  error: unknown,
  context?: Parameters<SentryModule['captureException']>[1],
): void {
  if (!sentry) return;
  sentry.captureException(error, context);
}

export function isErrorTrackingReady(): boolean {
  return initialized;
}
